import {ReactNode, useEffect, useRef, useState} from 'react'
import {CircularProgress} from '@mui/material'
import {toast} from 'react-toastify'
import {caronaApi} from '../../../services/CaronaApi.service'
import {AssistantApi} from '../../../services/AssistantApi.service'
import {AtaDetalhe, AtaResumo, DocumentoBloco, ItemAta, ItemComparado, JustificativaOut, RespostasEntrevista} from '../types'
import {ESFERA_LABEL, fmtDate, rotuloRequisito, rotuloStatus} from '../ui'
import DicasLoading from './DicasLoading'

/** Título da seção (reenquadrada para o ETP — Acórdão 547/2026-TCU). */
const TITULO_SECAO = 'Análise de Alternativas e Justificativa da Escolha da Ata'

/** Faixa (mín., máx.) de preço unitário registrado nos itens da ata — referência
 * do preço da ata escolhida no quadro comparativo. null quando não há itens com preço. */
function faixaPreco(itens: ItemAta[]): [number, number] | null {
    const vs = (itens || [])
        .map((i) => i.valor_unitario)
        .filter((v): v is number => v != null && v > 0)
    return vs.length ? [Math.min(...vs), Math.max(...vs)] : null
}

/** Preço registrado (referência) de uma alternativa, a partir dos campos opcionais
 * preco_min/preco_max que o backend enriquece (unitário ou faixa). null quando o
 * preço não foi confirmado no PNCP → o quadro mostra '—' (honesto). */
function precoAlternativa(a: AtaResumo): string | null {
    const min = a.preco_min
    const max = a.preco_max
    if (min == null || max == null) return null
    return min === max ? brl(min) : `${brl(min)} a ${brl(max)}`
}

/** Casa o ITEM EXATO cotejado por numero_controle da ata, unindo as duas fontes que o
 * backend agora entrega no detalhe da justificativa: `sugestoes_alternativas`
 * (primária — cobre a escolhida por `eh_escolhida` e todas as alternativas) e
 * `alerta_preco` (escolhida + `mais_baratas`, complementar). Chave = numero_controle
 * (== numero_controle_pncp da ata da linha; a escolhida == ata_numero_controle). Mapa
 * vazio (deploy assíncrono sem os campos novos) → o quadro degrada para o intervalo. */
function itensComparadosPorControle(
    j: JustificativaOut
): Map<string, {item: ItemComparado | null; incerto: boolean}> {
    const m = new Map<string, {item: ItemComparado | null; incerto: boolean}>()
    for (const s of j.sugestoes_alternativas || []) {
        if (!s.numero_controle) continue
        m.set(s.numero_controle, {item: s.item_comparado ?? null, incerto: Boolean(s.item_comparado_incerto)})
    }
    const ap = j.alerta_preco
    if (ap) {
        const escolhida = j.ata_numero_controle
        if (escolhida && !m.has(escolhida) && ap.escolhida_item_comparado) {
            m.set(escolhida, {item: ap.escolhida_item_comparado, incerto: Boolean(ap.escolhida_item_comparado_incerto)})
        }
        for (const mb of ap.mais_baratas || []) {
            if (mb.numero_controle && !m.has(mb.numero_controle) && mb.item_comparado) {
                m.set(mb.numero_controle, {item: mb.item_comparado, incerto: Boolean(mb.item_comparado_incerto)})
            }
        }
    }
    return m
}

/** Célula "Preço registrado (referência)" do quadro comparativo: exibe o ITEM EXATO
 * cotejado (comparação justa — não mascara itens diferentes) no lugar do intervalo,
 * no formato "R$ X — descrição (UN)". Regras:
 *  - `hit` ausente (contrato novo ainda não chegou — deploy assíncrono) → `fallback`
 *    mantém o comportamento atual (o intervalo), sem quebrar;
 *  - `hit.item == null` (contrato presente, mas o backend não determinou o item) →
 *    "não disponível";
 *  - `hit.incerto` → aviso pequeno "conferir item" ao lado (o item pode ser outro);
 *  - `href` (link_pncp da ata da linha) → o "conferir item" vira LINK clicável
 *    para a ata no PNCP (nova aba). */
function CelulaPreco({hit, fallback, href}: {
    hit?: {item: ItemComparado | null; incerto: boolean}
    fallback: ReactNode
    href?: string | null
}) {
    if (!hit) return <>{fallback}</>
    const it = hit.item
    if (!it) return <>não disponível</>
    const valor = it.valor_unitario != null ? brl(it.valor_unitario) : null
    const desc = (it.descricao || '').trim()
    const unidade = (it.unidade || '').trim() ? ` (${it.unidade})` : ''
    if (!valor && !desc) return <>não disponível</>
    const tituloConferir = 'O item exato cotejado não pôde ser confirmado automaticamente — confira antes de usar no ETP.'
    return (
        <>
            {valor && <strong>{valor}</strong>}
            {valor && desc ? ' — ' : ''}
            {desc ? <>{desc}{unidade}</> : ''}
            {hit.incerto && (
                <>
                    {' '}
                    {href ? (
                        <a
                            className="pendente"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={tituloConferir}
                            style={{fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap'}}
                        >
                            conferir item
                        </a>
                    ) : (
                        <span
                            className="pendente"
                            title={tituloConferir}
                            style={{fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap'}}
                        >
                            conferir item
                        </span>
                    )}
                </>
            )}
        </>
    )
}

interface Props {
    justificativa: JustificativaOut
    ata: AtaDetalhe | null
    respostas: RespostasEntrevista
    evidencias: Record<string, string>
    aderenteNome: string
    /** Outras atas do mesmo objeto que a busca trouxe — alternativas a comparar na
     * seção do ETP (Acórdão 547/2026-TCU). Opcional (histórico/testes). */
    alternativas?: AtaResumo[]
    /** Documento de planejamento (ETP/DFD/TR) enviado na entrevista — vira contexto
     * da Licito.Guru IA na redação (opcional; null = fluxo sem documento). */
    documentoId?: string | null
    /** Registro REABERTO do histórico (vs. geração nova da entrevista). Regra de
     * produto: reaberto com a redação nunca rodada NÃO auto-dispara a IA — mostra
     * o documento como está + botão explícito "Redigir com Licito.Guru IA". */
    reaberta?: boolean
    /** A espera pela redação esgotou os refetches de reconciliação: estado
     * honesto ("demorando mais que o normal") com Atualizar manual. */
    esperaEsgotada?: boolean
    /** Registro antigo sem os dados da entrevista: "Voltar à entrevista" fica
     * desabilitado com tooltip honesto em vez de quebrar. */
    entrevistaIndisponivel?: boolean
    /** Arma a espera pelo push do evento ia_texto (+ o único refetch de
     * reconciliação) no CaronaManager. */
    onAguardarIa?: (id: string) => void
    /** Refetch imediato do detalhe (ex.: a redação já estava CONCLUIDA). */
    onRecarregar?: (id: string) => void
    /** Handoff ao módulo de Pesquisa de Preços (IN 65) pré-preenchido com o objeto
     * da ata; a pesquisa criada volta vinculada a esta justificativa. */
    onPesquisarPrecos?: () => void
    /** Abre o módulo de Pesquisa de Preços SEM re-armar o handoff (só navegar —
     * para consultar a pesquisa já vinculada). */
    onAbrirPesquisas?: () => void
    onVoltarEntrevista: () => void
    onNovaBusca: () => void
}

/** Formata em reais pt-BR (mesma convenção do backend). */
function brl(v: number): string {
    return v.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
}

/** Fragmento copiável com os valores REAIS da pesquisa (espelha o domínio do
 * backend): "pesquisa de preços própria realizada na forma do art. 23...". */
function textoEvidenciaPesquisa(valores: Record<string, number>): string {
    const partes = [
        'pesquisa de preços própria realizada na forma do art. 23 da Lei nº 14.133/2021',
    ]
    const {minimo, maximo, mediana, total} = valores
    if (minimo != null && maximo != null) {
        partes.push(
            minimo === maximo
                ? `com valor unitário de referência apurado de ${brl(minimo)}`
                : `com valores unitários de referência apurados de ${brl(minimo)} a ${brl(maximo)}`
        )
        if (mediana != null) partes.push(`mediana de ${brl(mediana)}`)
    }
    if (total != null) partes.push(`e valor total estimado de ${brl(total)}`)
    return partes.join(', ')
}

function Pend({children}: {children: ReactNode}) {
    return <span className="pendente">[PENDENTE: {children}]</span>
}

/** "slug" amigável p/ nome de arquivo: sem acento, minúsculo, só a-z0-9 e hífen. */
function slug(s: string, max = 40): string {
    const base = (s || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    return base.slice(0, max).replace(/-+$/g, '')
}

/** Lê o documento (editável) do DOM e monta os blocos para o backend gerar o .docx.
 * Exportada para o teste de round-trip (bloco tabela ↔ <table>). */
export function blocosDoDom(root: HTMLElement): {titulo: string; subtitulo: string; blocos: DocumentoBloco[]} {
    let titulo = TITULO_SECAO
    let subtitulo = ''
    const blocos: DocumentoBloco[] = []
    Array.from(root.children).forEach((el) => {
        const tag = el.tagName
        const txt = (el.textContent || '').replace(/\s+/g, ' ').trim()
        if (el.classList.contains('doc-title')) {
            if (txt) titulo = txt
        } else if (el.classList.contains('doc-sub')) {
            subtitulo = txt
        } else if (tag === 'H2') {
            if (txt) blocos.push({nivel: 'h', texto: txt})
        } else if (tag === 'TABLE') {
            // Tabela é DADO (cálculo determinístico, ex.: teto de 50% por item):
            // preserva linhas/células como bloco `tabela` — nunca achatar em texto
            // corrido. 1ª linha = cabeçalho (contrato do bloco).
            const linhas: string[][] = []
            el.querySelectorAll('tr').forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll('th,td'))
                    .map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim())
                if (cells.some(Boolean)) linhas.push(cells)
            })
            if (linhas.length) blocos.push({nivel: 'tabela', texto: '', linhas})
        } else if (txt) {
            blocos.push({nivel: 'p', texto: txt})
        }
    })
    return {titulo, subtitulo, blocos}
}

/** Realça os marcadores [PENDENTE: …] no texto redigido pela IA. */
function Realce({texto}: {texto: string}) {
    const partes = texto.split(/(\[PENDENTE:[^\]]*\])/g)
    return (
        <>
            {partes.map((p, i) =>
                p.startsWith('[PENDENTE:') ? <span key={i} className="pendente">{p}</span> : p
            )}
        </>
    )
}

/** Bloco `tabela` (dado determinístico — nunca reescrito pela IA): desenha a
 * <table> real do documento. 1ª linha = cabeçalho (contrato do bloco). */
function TabelaDoc({linhas}: {linhas: string[][]}) {
    if (!linhas.length) return null
    const [cab, ...corpo] = linhas
    return (
        <table className="doc-table">
            <thead>
                <tr>{cab.map((c, i) => <th key={i}>{c}</th>)}</tr>
            </thead>
            <tbody>
                {corpo.map((linha, i) => (
                    <tr key={i}>{linha.map((c, j) => <td key={j}><Realce texto={c} /></td>)}</tr>
                ))}
            </tbody>
        </table>
    )
}

export default function JustificativaView(
    {justificativa, ata, respostas, evidencias, aderenteNome, alternativas = [],
     documentoId = null,
     reaberta = false, esperaEsgotada = false, entrevistaIndisponivel = false,
     onAguardarIa, onRecarregar, onPesquisarPrecos, onAbrirPesquisas,
     onVoltarEntrevista, onNovaBusca}: Props
) {
    const apta = justificativa.status === 'APTA'
    const comRisco = justificativa.status === 'COM_RISCO'
    const bannerCls = apta ? 'status-banner--apta' : comRisco ? 'status-banner--risco' : 'status-banner--erro'

    const e = respostas
    const itens = ata?.itens || []
    const temEv = (id: string) => Boolean((evidencias[id] || '').trim())
    const vigente = ata ? ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia) : false

    // Pesquisa de Preços vinculada (o GET de detalhe já traz status/valores por
    // leitura best-effort; status null = aguardando dados). Fonte de verdade =
    // `pesquisa_precos`; os campos do CALLBACK (pesquisa_valores/
    // pesquisa_orcamento_estimado) são OPCIONAIS e, quando presentes, têm
    // preferência na exibição do orçamento.
    const pesquisa = justificativa.pesquisa_precos || null
    const pesquisaConcluida = Boolean(pesquisa && pesquisa.concluida && pesquisa.valores)
    const pesquisaValores = justificativa.pesquisa_valores ?? pesquisa?.valores ?? null
    const pesquisaOrcamento = justificativa.pesquisa_orcamento_estimado ?? pesquisaValores?.total ?? null

    // Rascunho determinístico: ANDAIME INTERNO (oculto) — carrega os fatos e os
    // [PENDENTE] que alimentam a IA. NUNCA aparece como texto final para o usuário.
    const scaffoldRef = useRef<HTMLDivElement>(null)
    // Documento visível: SEMPRE a versão redigida pela Licito.guru IA (editável).
    const docRef = useRef<HTMLDivElement>(null)
    const [baixando, setBaixando] = useState<'' | 'docx' | 'pdf'>('')
    const [feedbackModal, setFeedbackModal] = useState(false)
    const [feedbackText, setFeedbackText] = useState('')
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)

    // Estado LOCAL da redação (cobre a janela entre o POST e o refetch): o estado
    // durável vem do registro (justificativa.ia_texto_status).
    const [iaLocal, setIaLocal] = useState<'' | 'PROCESSANDO' | 'FALHOU'>('')
    const statusIa = iaLocal || justificativa.ia_texto_status || ''
    const iaRodou = useRef(false)
    // Documento que REALMENTE entrou como contexto da redação disparada nesta
    // tela — o chip só menciona o documento quando ele de fato foi enviado.
    const docUsadoRef = useRef<string | null>(null)

    // Registro fresco chegou (push/reconciliação): estado durável manda de novo.
    useEffect(() => {
        const st = justificativa.ia_texto_status
        if (st === 'CONCLUIDO' || st === 'FALHOU') setIaLocal('')
    }, [justificativa])

    // A redação dispara AUTOMATICAMENTE só na geração NOVA (fluxo da entrevista).
    // Registro REABERTO do histórico com a redação nunca rodada mostra o
    // documento como está (histórico íntegro) e oferece o botão explícito
    // "Redigir com Licito.Guru IA" — regra de produto.
    useEffect(() => {
        if (iaRodou.current) return
        iaRodou.current = true
        if (!justificativa.ia_texto_status && !reaberta) redigirComIa()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Registro reaberto sem redação: o rascunho (andaime) é exibido como o
    // documento do histórico, em vez do spinner/auto-disparo.
    const mostrarDocHistorico = reaberta && statusIa === ''
    // Espera esgotada (2 refetches sem desfecho) e ainda sem estado terminal.
    const mostrarDemora = esperaEsgotada && statusIa !== 'CONCLUIDO' && statusIa !== 'FALHOU'

    // Nome de arquivo personalizado p/ a justificativa: objeto + nº da ata (sem extensão).
    const nomeArquivo = (() => {
        const partes = ['justificativa-adesao']
        if (ata?.objeto) partes.push(slug(ata.objeto))
        if (ata?.numero_ata) partes.push('ata-' + slug(ata.numero_ata))
        if (partes.length === 1) partes.push(new Date().toISOString().slice(0, 10))
        return partes.join('-')
    })()

    async function baixar(formato: 'docx' | 'pdf') {
        if (!docRef.current) return
        const {titulo, subtitulo, blocos} = blocosDoDom(docRef.current) // captura edições antes do re-render
        setBaixando(formato)
        try {
            const blob = await caronaApi.gerarDocumento({titulo, subtitulo, blocos}, formato)
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${nomeArquivo}.${formato}`
            document.body.appendChild(a)
            a.click()
            a.remove()
            URL.revokeObjectURL(url)
        } catch (err: any) {
            toast.error(`Falha ao gerar o ${formato.toUpperCase()} (${err?.message || 'erro'}).`)
        } finally {
            setBaixando('')
        }
    }

    async function enviarFeedback() {
        if (!feedbackText.trim()) return
        setFeedbackSubmitting(true)
        try {
            const assistantApi = new AssistantApi()
            await assistantApi.reportModule({
                object_id: justificativa.id,
                module: 'smart_minutes',
                title: ata?.objeto || 'Justificativa de adesão',
                feedback: feedbackText,
            })
            toast.success('Obrigado pelo feedback.')
            setFeedbackModal(false)
            setFeedbackText('')
        } catch (err: any) {
            toast.error(`Falha ao enviar feedback (${err?.message || 'erro'}).`)
        } finally {
            setFeedbackSubmitting(false)
        }
    }

    /** PDF no próprio navegador (instantâneo, sem servidor): imprime só o documento
     * via @media print → o usuário escolhe "Salvar como PDF". Captura também as edições. */
    function imprimirPdf() {
        const tituloOriginal = document.title
        document.title = nomeArquivo
        window.print()
        setTimeout(() => { document.title = tituloOriginal }, 800)
    }

    /** Texto plano da seção (título + blocos redigidos pela IA) — o que o gestor
     * cola no Word do ETP. Tabelas viram linhas com células separadas por TAB. */
    function textoDaSecao(): string {
        const linhas: string[] = [TITULO_SECAO, '']
        for (const b of justificativa.ia_texto_blocos || []) {
            if (b.nivel === 'tabela') {
                for (const linha of b.linhas || []) linhas.push(linha.join('\t'))
            } else if ((b.texto || '').trim()) {
                linhas.push(b.texto)
            }
        }
        return linhas.join('\n')
    }

    /** Copia a seção para a área de transferência (para colar no ETP). */
    async function copiarSecao() {
        try {
            await navigator.clipboard.writeText(textoDaSecao())
            toast.success('Seção copiada. Cole no seu ETP (Estudo Técnico Preliminar).')
        } catch {
            toast.error('Não foi possível copiar automaticamente. Selecione o texto e copie manualmente.')
        }
    }

    // Alternativas (para a seção de escolha da ata) e o preço registrado da ata escolhida.
    const precoEscolhida = faixaPreco(itens)
    // Item EXATO cotejado por ata (join por numero_controle) — o quadro mostra o item
    // real no lugar do intervalo. Barato de recomputar (poucas entradas).
    const itensComparados = itensComparadosPorControle(justificativa)
    const consideradas = (respostas.alternativas_consideradas || []).filter(
        (a) => (a.referencia || '').trim() || (a.motivo || '').trim()
    )
    const obsAlternativas = (respostas.alternativas_observacao || '').trim()

    /** Dispara a redação pela Licito.guru IA — ASSÍNCRONA. O rascunho determinístico
     * (andaime oculto) vai como insumo; a resposta é 202 e o texto chega por push
     * (notifier) + refetch do detalhe. Com documento de planejamento anexado, ele
     * entra como CONTEXTO da IA (o backend monta o prompt com as âncoras jurídicas). */
    async function redigirComIa() {
        if (!scaffoldRef.current) return
        const {titulo, subtitulo, blocos} = blocosDoDom(scaffoldRef.current)
        setIaLocal('PROCESSANDO')
        // O documento é do FLUXO da entrevista corrente: registro reaberto do
        // histórico NUNCA envia documento_id de outro fluxo.
        const docId = reaberta ? null : documentoId
        docUsadoRef.current = docId
        try {
            const r = await caronaApi.redigirJustificativa({
                titulo, subtitulo, blocos,
                justificativa_id: justificativa.id,
                ...(docId ? {documento_id: docId} : {}),
            })
            if (r?.status === 'CONCLUIDO') {
                // Já estava redigida (ex.: outra aba concluiu) — busca os blocos.
                onRecarregar?.(justificativa.id)
            } else {
                onAguardarIa?.(justificativa.id)
            }
        } catch (err: any) {
            if (err?.response?.status === 409) {
                // Já há uma redação em andamento (clique duplo/2ª aba): só aguarda.
                onAguardarIa?.(justificativa.id)
            } else {
                setIaLocal('FALHOU') // estado honesto; o botão "Refazer" repõe o POST
            }
        }
    }

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onNovaBusca}>Ata Inteligente</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <a onClick={entrevistaIndisponivel ? undefined : onVoltarEntrevista}>Entrevista</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Justificativa gerada</span>
                </div>
                <div className="header-row">
                    <h1 className="lg-h2">Justificativa de adesão</h1>
                    <button
                        className="lg-btn lg-btn--secondary lg-btn--sm"
                        onClick={entrevistaIndisponivel ? undefined : onVoltarEntrevista}
                        disabled={entrevistaIndisponivel}
                        title={entrevistaIndisponivel ? 'Registro antigo sem os dados da entrevista' : undefined}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                        Voltar à entrevista
                    </button>
                </div>
            </header>

            <div className="page-content" style={{maxWidth: 920}}>
                <div className={`status-banner ${bannerCls}`}>
                    <span className="status-banner__icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            dangerouslySetInnerHTML={{__html: apta ? '<path d="M20 6 9 17l-5-5"/>' : '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>'}} />
                    </span>
                    <div>
                        <div className="status-banner__title">
                            {apta ? 'Justificativa APTA' : comRisco ? `Justificativa COM RISCO — ${justificativa.riscos.length} pendência(s)` : 'Erro ao gerar'}
                        </div>
                        <div className="status-banner__desc">
                            {apta
                                ? 'Todos os requisitos obrigatórios foram satisfeitos. Documento pronto para o parecer jurídico e o controle prévio.'
                                : comRisco
                                ? 'O status APTA é atribuído somente quando todos os requisitos têm evidência. A minuta foi gerada com marcações [PENDENTE] nos pontos que ainda dependem de comprovação.'
                                : 'Não foi possível concluir o processamento. Tente novamente.'}
                        </div>
                    </div>
                </div>

                {/* Seção para o ETP + alerta de responsabilidade (UI apenas — nunca entra
                    no texto copiável). A Licito.guru IA é apoio; a análise e a decisão são
                    do agente público responsável, que verifica as informações. */}
                <div className="callout callout--info" style={{marginBottom: 16}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <div>
                        <span className="callout__title">Seção para inserir no seu ETP (Estudo Técnico Preliminar)</span>
                        Este é o conteúdo da <strong>análise de alternativas e justificativa da escolha da ata</strong> para você colar no ETP do órgão — não é um documento avulso. Copie ou baixe apenas esta seção; o restante do ETP permanece como está.
                    </div>
                </div>
                <div className="callout callout--warn" style={{marginBottom: 22}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <div>
                        <span className="callout__title">A Licito.guru IA é uma ferramenta de apoio</span>
                        A comparação das alternativas e a decisão de aderir são de responsabilidade do <strong>agente público</strong>, que deve conferir os dados (preços, vigências e condições das atas) e confirmar a fundamentação antes de incorporá-la ao processo.
                    </div>
                </div>

                {/* Alerta (NÃO bloqueante) de ata alternativa mais barata — fonte única
                    (sinal determinístico do backend). UI apenas: nunca entra no texto
                    copiável. Acórdão 547/2026-TCU (§ 73): comparar por preço; aderir à
                    mais cara existindo alternativa mais barata, sem justificar, é o que
                    o TCU pune. O usuário decide — nada trava. */}
                {justificativa.alerta_preco && justificativa.alerta_preco.mais_baratas.length > 0 && (() => {
                    const ap = justificativa.alerta_preco!
                    const un = ap.unidade_base ? `/${ap.unidade_base}` : ''
                    const uma = ap.mais_baratas.length === 1
                    return (
                        <div className="callout callout--warn" role="alert" style={{marginBottom: 22}}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            <div>
                                <span className="callout__title">
                                    Atenção: {uma ? 'há uma ata com' : 'há atas com'} preço registrado inferior
                                </span>
                                Considerando o preço por unidade comparável{ap.unidade_base ? ` (${ap.unidade_base})` : ''} do mesmo item, {uma ? 'a ata a seguir registra' : 'as atas a seguir registram'} preço menor que o da ata escolhida (<strong>{brl(ap.escolhida_preco_base)}{un}</strong>):
                                <ul style={{margin: '8px 0 8px 18px'}}>
                                    {ap.mais_baratas.map((m) => (
                                        <li key={m.numero_controle}>
                                            Ata {m.numero_ata} (órgão {m.orgao || 'não informado'}{m.uf ? `/${m.uf}` : ''}): <strong>{brl(m.preco_base)}{un}</strong>
                                        </li>
                                    ))}
                                </ul>
                                Considere essa informação na sua decisão (ver o quadro comparativo abaixo): a escolha deve fundamentar-se nos demais critérios do § 73 (condições contratuais, logística, prazos e compatibilidade) ou ser reavaliada. <strong>A decisão é sua</strong> — a Licito.guru IA não decide.
                            </div>
                        </div>
                    )
                })()}

                {justificativa.riscos.length > 0 && (
                    <div className="detail-panel" style={{marginBottom: 22}}>
                        <div className="detail-panel__title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--feedback-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            Relatório de pendências
                        </div>
                        <div className="risco-list">
                            {justificativa.riscos.map((r, i) => (
                                <div key={i} className="risco-item">
                                    <span className={`risco-item__sev sev--${(r.severidade || 'media').toLowerCase()}`}>{r.severidade}</span>
                                    <div>
                                        <div className="risco-item__req">{rotuloRequisito(r.requisito_id)} · {r.base_legal}</div>
                                        <div className="risco-item__msg">{r.mensagem}</div>
                                        {r.requisito_id === 'vantajosidade' && !r.informativo && onPesquisarPrecos && (
                                            <button
                                                className="lg-btn lg-btn--brand lg-btn--sm"
                                                style={{marginTop: 8}}
                                                onClick={onPesquisarPrecos}
                                            >
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                                Pesquisar preços (IN 65)
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ---- Pesquisa de Preços vinculada: card com status/valores (o GET de
                     detalhe traz os dados por leitura best-effort — sem polling). ---- */}
                {pesquisa && (
                    <div className="detail-panel" style={{marginBottom: 22}}>
                        <div className="detail-panel__title">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                            Pesquisa de preços vinculada (IN 65)
                        </div>
                        <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
                            <div style={{flex: 1, minWidth: 240}}>
                                <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-primary)'}}>
                                    {pesquisaConcluida
                                        ? <strong>Concluída</strong>
                                        : pesquisa.status
                                        ? <strong>Em andamento</strong>
                                        : <strong>Vinculada — aguardando dados</strong>}
                                    {pesquisa.objeto ? <> · {pesquisa.objeto}</> : null}
                                </div>
                                {pesquisaConcluida && (
                                    <>
                                        {pesquisaOrcamento != null && (
                                            <div style={{fontSize: 'var(--fs-lg)', color: 'var(--brand-navy)', marginTop: 6}}>
                                                Orçamento estimado: <strong>{brl(pesquisaOrcamento)}</strong>
                                            </div>
                                        )}
                                        {pesquisaValores && (
                                            <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginTop: 4}}>
                                                {pesquisaValores.minimo != null && <>mínimo {brl(pesquisaValores.minimo)}</>}
                                                {pesquisaValores.mediana != null && <> · mediana {brl(pesquisaValores.mediana)}</>}
                                                {pesquisaValores.maximo != null && <> · máximo {brl(pesquisaValores.maximo)}</>}
                                            </div>
                                        )}
                                    </>
                                )}
                                {!pesquisaConcluida && (
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8, marginTop: 6}}>
                                        <CircularProgress size={16} aria-label="Pesquisa de preços em andamento" />
                                        <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                            {pesquisa.status
                                                ? 'Pesquisa de preços em andamento — o orçamento estimado aparecerá aqui automaticamente.'
                                                : 'A pesquisa está vinculada; os dados aparecem aqui assim que disponíveis.'}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {onAbrirPesquisas && (
                                <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onAbrirPesquisas}>
                                    Abrir Pesquisa de Preços
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* ---- Texto da justificativa: SEMPRE a versão redigida pela Licito.guru
                     IA. O rascunho determinístico (mais abaixo, oculto) é andaime
                     interno: alimenta a IA e nunca aparece como texto final. ---- */}
                {statusIa === 'CONCLUIDO' && (
                    <div className="doc-edit-hint" style={{justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                            Primeira versão <strong>redigida pela Licito.Guru IA</strong> a partir da entrevista{docUsadoRef.current ? ' e do documento de planejamento anexado (usado só como contexto — não é copiado nem anexado ao texto)' : ''} — edite à vontade.
                        </span>
                    </div>
                )}
                {statusIa === 'CONCLUIDO' && (
                    <div className="doc-paper" ref={docRef} contentEditable suppressContentEditableWarning>
                        <div className="doc-title">{TITULO_SECAO}</div>
                        <div className="doc-sub">Seção para inserir no Estudo Técnico Preliminar (ETP) · {aderenteNome} · {ESFERA_LABEL[e.esfera_aderente]} · adesão por órgão não participante (carona)</div>
                        {(justificativa.ia_texto_blocos || []).map((b, i) => b.nivel === 'h'
                            ? <h2 key={i} className="doc-h">{b.texto}</h2>
                            : b.nivel === 'tabela'
                            ? <TabelaDoc key={i} linhas={b.linhas || []} />
                            : <p key={i} className="doc-p"><Realce texto={b.texto} /></p>)}
                    </div>
                )}
                {mostrarDocHistorico && (
                    <div className="doc-edit-hint" style={{justifyContent: 'space-between', alignItems: 'center', gap: 10}}>
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            Documento do histórico — a redação pela Licito.Guru IA ainda não foi feita para este registro.
                        </span>
                        <button
                            className="lg-btn lg-btn--brand lg-btn--sm"
                            onClick={() => redigirComIa()}
                            disabled={entrevistaIndisponivel}
                            title={entrevistaIndisponivel ? 'Registro antigo sem os dados da entrevista' : undefined}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></svg>
                            Redigir com Licito.Guru IA
                        </button>
                    </div>
                )}
                {statusIa !== 'CONCLUIDO' && statusIa !== 'FALHOU' && !mostrarDocHistorico && !mostrarDemora && (
                    <div className="doc-paper" aria-busy="true">
                        <DicasLoading titulo="A Licito.guru IA está redigindo a justificativa…" />
                    </div>
                )}
                {mostrarDemora && (
                    <div className="doc-paper" style={{textAlign: 'center', padding: '48px 24px'}}>
                        <p className="doc-p" style={{textAlign: 'center', marginBottom: 18}}>
                            A redação está demorando mais que o normal.
                        </p>
                        <button className="lg-btn lg-btn--brand" onClick={() => onRecarregar?.(justificativa.id)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                            Atualizar
                        </button>
                    </div>
                )}
                {statusIa === 'FALHOU' && (
                    <div className="doc-paper" style={{textAlign: 'center', padding: '48px 24px'}}>
                        <p className="doc-p" style={{textAlign: 'center', marginBottom: 18}}>
                            Não foi possível concluir a redação pela Licito.Guru IA.
                        </p>
                        <button className="lg-btn lg-btn--brand" onClick={() => redigirComIa()}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/><path d="M19 14l.7 1.8L21.5 16.5 19.7 17.2 19 19l-.7-1.8L16.5 16.5 18.3 15.8 19 14z"/></svg>
                            Refazer com Licito.Guru IA
                        </button>
                    </div>
                )}
                {/* Andaime determinístico: fonte dos blocos enviados à IA. Fica
                    VISÍVEL só no registro reaberto sem redação (documento do
                    histórico, dos dados reais); oculto nos demais estados. */}
                <div
                    className="doc-paper" ref={scaffoldRef}
                    style={mostrarDocHistorico ? undefined : {display: 'none'}}
                    aria-hidden={mostrarDocHistorico ? undefined : true}
                >
                    <div className="doc-title">{TITULO_SECAO}</div>
                    <div className="doc-sub">Seção para inserir no Estudo Técnico Preliminar (ETP) · {aderenteNome} · {ESFERA_LABEL[e.esfera_aderente]} · adesão por órgão não participante (carona)</div>

                    <h2 className="doc-h">1. Necessidade e motivação</h2>
                    <p className="doc-p">
                        {e.demanda_descricao.trim() || <Pend>descrever a demanda concreta do órgão</Pend>}{' '}
                        {e.demanda_prevista_pca ? 'A demanda está prevista no planejamento da contratação (PCA/DFD).' : <>A previsão no planejamento (PCA/DFD) não foi confirmada: <Pend>referência no PCA/DFD</Pend>.</>}
                        {e.alega_urgencia && (temEv('urgencia_desabastecimento')
                            ? <> Justifica-se a urgência (evidência: {evidencias['urgencia_desabastecimento']}).</>
                            : <> Há urgência alegada, mas <Pend>fundamento/evidência da urgência</Pend>.</>)}
                    </p>

                    <h2 className="doc-h">2. Fundamentação legal</h2>
                    <p className="doc-p">
                        A adesão encontra amparo nos arts. 82 a 86 da Lei nº 14.133/2021 e no Decreto nº 11.462/2023,
                        que regulamentam o Sistema de Registro de Preços e a contratação por órgão não participante,
                        observados os limites e vedações dos §§ 3º, 4º, 7º e 8º do art. 86 e a vigência do art. 84.
                    </p>

                    <h2 className="doc-h">3. Demonstração da vantajosidade</h2>
                    <p className="doc-p">
                        {pesquisaConcluida && pesquisa?.valores
                            /* Pesquisa vinculada CONCLUÍDA: o [PENDENTE] dá lugar aos VALORES
                               REAIS (texto copiável — cita a pesquisa pelo que ela é no
                               processo, nunca a ferramenta). */
                            ? <>A vantajosidade da adesão está demonstrada por {textoEvidenciaPesquisa(pesquisa.valores)}. Os preços registrados na ata mostram-se compatíveis com os parâmetros de mercado apurados, atendida a exigência de pesquisa própria e atualizada do órgão aderente (TCU, Acórdãos 420/2018-Plenário e 1794/2023-1ª Câmara).</>
                            : e.declara_preco_compativel
                            ? <>O responsável declara que o preço registrado é compatível com o parâmetro de mercado (modo autônomo, v1). Recomenda-se, para maior segurança jurídica, pesquisa de preços própria e atualizada do órgão aderente, em observância ao TCU (Acórdãos 420/2018-Plenário e 1794/2023-1ª Câmara), que veda aferir a vantajosidade pela mera comparação com os preços da própria ata.{pesquisa ? <> Registra-se que há pesquisa de preços própria em andamento, realizada na forma do art. 23 da Lei nº 14.133/2021, cujos valores de referência complementarão esta demonstração quando concluída.</> : null}</>
                            : pesquisa
                            /* Vinculada mas ainda sem valores: a pendência NÃO some — em andamento. */
                            ? <><Pend>vantajosidade — pesquisa de preços em andamento</Pend> — Há pesquisa de preços própria em curso, realizada na forma do art. 23 da Lei nº 14.133/2021. A demonstração da vantajosidade será concluída com os valores de referência apurados; até lá, a comparação restrita aos preços registrados na ata é insuficiente (TCU 420/2018 e 1794/2023).</>
                            : <><Pend>vantajosidade não comprovada</Pend> — A demonstração da vantajosidade exige pesquisa de preços própria e atualizada (TCU 420/2018 e 1794/2023). A comparação restrita aos preços registrados na ata é insuficiente e não pode embasar a adesão.</>}
                    </p>

                    <h2 className="doc-h">4. Análise de alternativas e justificativa da escolha da ata</h2>
                    <p className="doc-p">
                        A decisão de aderir a esta Ata de Registro de Preços e a escolha, dentre as
                        alternativas disponíveis, decorrem de análise comparativa realizada neste Estudo
                        Técnico Preliminar, na forma do art. 18, § 1º, V, da Lei nº 14.133/2021 e do
                        entendimento do TCU (Acórdão 547/2026-Plenário, § 74), considerados os critérios de
                        objeto, preço registrado, condições contratuais e logística (§ 73).
                    </p>
                    {alternativas.length > 0 ? (
                        <>
                            <p className="doc-p">
                                Foram identificadas outras atas de registro de preços com objeto compatível,
                                cotejadas no quadro a seguir:
                            </p>
                            <table className="doc-table">
                                <thead><tr>
                                    <th>Ata</th>
                                    <th>Órgão gerenciador (UF/esfera)</th>
                                    <th>Vigência</th>
                                    <th>Preço registrado (referência)</th>
                                    <th>Situação</th>
                                </tr></thead>
                                <tbody>
                                    <tr>
                                        <td>{ata?.numero_ata || ata?.numero_controle_pncp || '—'}</td>
                                        <td>{ata?.orgao || '—'}{ata?.uf ? ` (${ata.uf}/${ata.esfera})` : ''}</td>
                                        <td>{fmtDate(ata?.vigencia_fim)}</td>
                                        <td>
                                            <CelulaPreco
                                                hit={itensComparados.get(ata?.numero_controle_pncp || justificativa.ata_numero_controle)}
                                                href={ata?.link_pncp}
                                                fallback={precoEscolhida
                                                    ? (precoEscolhida[0] === precoEscolhida[1]
                                                        ? brl(precoEscolhida[0])
                                                        : `${brl(precoEscolhida[0])} a ${brl(precoEscolhida[1])}`)
                                                    : 'não disponível'}
                                            />
                                        </td>
                                        <td>Ata escolhida</td>
                                    </tr>
                                    {alternativas.slice(0, 5).map((a) => (
                                        <tr key={a.numero_controle_pncp}>
                                            <td>{a.numero_ata || a.numero_controle_pncp}</td>
                                            <td>{a.orgao}{a.uf ? ` (${a.uf}/${a.esfera})` : ''}</td>
                                            <td>{fmtDate(a.vigencia_fim)}</td>
                                            <td>
                                                <CelulaPreco
                                                    hit={itensComparados.get(a.numero_controle_pncp)}
                                                    href={a.link_pncp}
                                                    fallback={precoAlternativa(a) ?? '—'}
                                                />
                                            </td>
                                            <td>Alternativa avaliada</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    ) : (
                        <p className="doc-p">
                            <Pend>relacionar e comparar as demais atas de registro de preços do mesmo objeto (preço registrado, órgão gerenciador, vigência e compatibilidade com a demanda)</Pend>.
                        </p>
                    )}
                    {consideradas.length > 0 && (
                        <p className="doc-p">
                            Alternativas consideradas e razões da escolha:{' '}
                            {consideradas.map((a, i) => (
                                <span key={i}>{a.referencia || 'alternativa'}{a.motivo ? ` — ${a.motivo}` : ''}{i < consideradas.length - 1 ? '; ' : '.'}</span>
                            ))}
                        </p>
                    )}
                    <p className="doc-p">
                        Quanto à alternativa de realizar licitação própria, a adesão evita a instauração de
                        novo procedimento licitatório, com ganho de celeridade e economicidade processual,
                        desde que demonstrada a vantajosidade de preço por pesquisa própria e atualizada, na
                        forma do art. 23 da Lei nº 14.133/2021{pesquisaConcluida ? ', já apurada nesta análise' : pesquisa ? ', objeto de pesquisa própria vinculada em conclusão' : ''}.
                        {consideradas.length === 0 && !obsAlternativas && <> <Pend>registrar por que a adesão é preferível à licitação própria no caso concreto</Pend>.</>}
                    </p>
                    {obsAlternativas && <p className="doc-p">{obsAlternativas}</p>}

                    <h2 className="doc-h">5. Verificação dos limites quantitativos</h2>
                    <p className="doc-p">
                        Os quantitativos pretendidos respeitam o limite individual de 50% por item (art. 86, §3º), conforme tabela.
                        {' '}Saldo coletivo (200%, §4º): <strong>{ata?.saldo_status === 'DISPONIVEL' ? 'disponível' : ata?.saldo_status === 'ESGOTADO' ? 'esgotado' : 'indeterminado'}</strong> — confirme com o órgão gerenciador.
                    </p>
                    <table className="doc-table">
                        <thead><tr>
                            <th>Item</th><th>Descrição</th>
                            <th style={{textAlign: 'right'}}>Registrado</th>
                            <th style={{textAlign: 'right'}}>Teto 50%</th>
                            <th style={{textAlign: 'right'}}>Pretendido</th>
                            <th style={{textAlign: 'center'}}>OK</th>
                        </tr></thead>
                        <tbody>
                            {(() => {
                                // Só os itens que o gestor pretende adquirir (qtd > 0 na entrevista).
                                const pretendidos = itens.filter((it) => (e.quantidades_desejadas[String(it.numero_item)] || 0) > 0)
                                if (pretendidos.length === 0) {
                                    return (
                                        <tr><td colSpan={6} style={{color: 'var(--text-tertiary)'}}>
                                            <Pend>informe na entrevista as quantidades pretendidas por item</Pend>
                                        </td></tr>
                                    )
                                }
                                return pretendidos.map((it) => {
                                    const teto = Math.floor(it.qtd_registrada * 0.5)
                                    const q = e.quantidades_desejadas[String(it.numero_item)] || 0
                                    const okq = q <= teto
                                    return (
                                        <tr key={it.numero_item}>
                                            <td>{it.numero_item}</td>
                                            <td>{it.descricao}</td>
                                            <td style={{textAlign: 'right'}}>{it.qtd_registrada.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'right'}}>{teto.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'right'}}>{q.toLocaleString('pt-BR')}</td>
                                            <td style={{textAlign: 'center'}}>{okq ? '✓' : '✕ excede'}</td>
                                        </tr>
                                    )
                                })
                            })()}
                        </tbody>
                    </table>

                    <h2 className="doc-h">6. Vigência</h2>
                    <p className="doc-p">
                        A Ata de Registro de Preços nº {ata?.numero_ata || '—'}, gerenciada por {ata?.orgao}, encontra-se{' '}
                        {vigente ? <>vigente até {fmtDate(ata?.vigencia_fim)}</> : <Pend>ata fora de vigência</Pend>}, atendido o requisito de adesão a ata vigente (art. 84).
                    </p>

                    <h2 className="doc-h">7. Anuências</h2>
                    <p className="doc-p">
                        {temEv('anuencia_gerenciador') ? 'Consta a autorização do órgão gerenciador.' : <><Pend>autorização do órgão gerenciador</Pend>.</>}{' '}
                        {temEv('aceite_fornecedor') ? 'Consta o aceite do fornecedor registrado.' : <><Pend>aceite do fornecedor registrado</Pend>.</>}{' '}
                        {(!temEv('anuencia_gerenciador') || !temEv('aceite_fornecedor')) && <em>A presente justificativa é emitida como minuta condicionada à obtenção das anuências pendentes.</em>}
                    </p>

                    <h2 className="doc-h">8. Conclusão</h2>
                    <p className="doc-p">
                        {apta
                            ? 'Satisfeitos os requisitos legais e jurisprudenciais, a adesão à presente Ata de Registro de Preços mostra-se regular e vantajosa, recomendando-se o prosseguimento para parecer jurídico (art. 53) e publicidade no PNCP, que permanecem a cargo da autoridade competente.'
                            : <>Há requisitos pendentes de comprovação (vide relatório de pendências). Esta minuta <strong>não autoriza a adesão</strong>: as lacunas marcadas como <span className="pendente">[PENDENTE]</span> devem ser supridas com a evidência correspondente antes do encaminhamento ao parecer jurídico.</>}
                    </p>
                </div>

                <div className="actions-bar">
                    <div className="match-score" style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
                        Status: <strong>{rotuloStatus(justificativa.status)}</strong> · gerada em {fmtDate(new Date().toISOString().slice(0, 10))}
                    </div>
                    <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
                        {/* Copiar/baixar entregam a versão da IA — habilitados só com a redação CONCLUIDA. */}
                        <button className="lg-btn lg-btn--brand" onClick={copiarSecao} disabled={statusIa !== 'CONCLUIDO'}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                            Copiar seção para o ETP
                        </button>
                        <button className="lg-btn lg-btn--secondary" onClick={imprimirPdf} disabled={statusIa !== 'CONCLUIDO'}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                            Salvar em PDF
                        </button>
                        <button className="lg-btn lg-btn--primary" onClick={() => baixar('docx')} disabled={!!baixando || statusIa !== 'CONCLUIDO'}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            {baixando === 'docx' ? 'Gerando…' : 'Baixar .docx'}
                        </button>
                        <button className="lg-btn lg-btn--secondary" onClick={() => setFeedbackModal(true)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            Enviar Feedback
                        </button>
                    </div>
                </div>

                <div className="disclaimer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <div>Este módulo <strong>instrui e documenta</strong>: não envia o pedido de adesão, não assina e não substitui o parecer jurídico (art. 53) nem a decisão da autoridade competente. Toda afirmação factual tem lastro nas respostas da entrevista — o documento não inventa datas, quantidades ou números de processo.</div>
                </div>
            </div>

            {feedbackModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    onClick={() => setFeedbackModal(false)}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 12,
                            padding: 24,
                            width: 460,
                            maxWidth: '90vw',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12}}>
                            <h3 style={{margin: 0, fontSize: 17}}>Enviar feedback</h3>
                            <button
                                className="lg-btn lg-btn--secondary lg-btn--sm"
                                onClick={() => setFeedbackModal(false)}
                                aria-label="Fechar"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                        <p style={{margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 14}}>
                            {ata?.objeto || 'Justificativa de adesão'}
                        </p>
                        <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Descreva sua análise sobre esta justificativa..."
                            rows={5}
                            style={{
                                width: '100%',
                                padding: 10,
                                borderRadius: 8,
                                border: '1px solid var(--border-default, #ccc)',
                                fontFamily: 'inherit',
                                fontSize: 14,
                                resize: 'vertical',
                                boxSizing: 'border-box',
                            }}
                        />
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16}}>
                            <button className="lg-btn lg-btn--secondary" onClick={() => setFeedbackModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className="lg-btn lg-btn--primary"
                                onClick={enviarFeedback}
                                disabled={!feedbackText.trim() || feedbackSubmitting}
                            >
                                {feedbackSubmitting ? 'Enviando…' : 'Enviar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
